"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import type { Profile, City } from "@/lib/data/types";
import { cn } from "@/lib/utils";
import { profileHref } from "@/lib/event-taxonomy";

export function GlobalSearch({
  className,
  showSuggestions = false,
  variant = "default",
}: {
  className?: string;
  showSuggestions?: boolean;
  variant?: "default" | "header";
}) {
  const t = useTranslations("search");
  const tHome = useTranslations("home");
  const router = useRouter();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedFromSearch, setSelectedFromSearch] = useState(false);
  const [results, setResults] = useState<{ profiles: Profile[]; cities: City[] }>({ profiles: [], cities: [] });
  const boxRef = useRef<HTMLDivElement>(null);
  const isHeader = variant === "header";

  useEffect(() => {
    if (!query.trim()) return;
    const controller = new AbortController();
    const t = setTimeout(() => {
      fetch(`${import.meta.env.VITE_API_URL ?? "/api/spa"}/search?q=${encodeURIComponent(query)}`, { signal: controller.signal })
        .then((r) => r.json())
        .then(setResults)
        .catch(() => {});
    }, 180);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [query]);

  const activeResults = query.trim() ? results : { profiles: [], cities: [] };

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const hasResults = activeResults.profiles.length > 0 || activeResults.cities.length > 0;

  function clearSearch() {
    setQuery("");
    setResults({ profiles: [], cities: [] });
    setOpen(false);
    if (isHeader && selectedFromSearch) {
      setSelectedFromSearch(false);
      router.push("/");
    }
  }

  function selectResult() {
    setSelectedFromSearch(true);
    setOpen(false);
  }

  return (
    <div ref={boxRef} className={cn("relative w-full", className)}>
      <div
        className={cn(
          "flex items-center rounded-full border border-green/25 bg-white/92 transition-colors focus-within:border-gold/70 focus-within:bg-white",
          isHeader
            ? "h-10 gap-2.5 px-3.5 shadow-none"
            : "gap-3 px-4 py-3 shadow-[0_22px_56px_-32px_rgb(var(--shadow-color)),inset_0_1px_0_rgba(255,255,255,0.9)] sm:px-5 sm:py-3.5"
        )}
      >
        <svg
          viewBox="0 0 24 24"
          className={cn("shrink-0 text-green", isHeader ? "h-4 w-4" : "h-5 w-5")}
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          aria-hidden="true"
        >
          <circle cx="11" cy="11" r="7" />
          <path d="M21 21l-4.3-4.3" strokeLinecap="round" />
        </svg>
        <input
          value={query}
          onChange={(e) => {
            const nextQuery = e.target.value;
            if (!nextQuery) {
              clearSearch();
              return;
            }
            setQuery(nextQuery);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          type="text"
          placeholder={tHome("searchPlaceholder")}
          aria-label={tHome("searchPlaceholder")}
          className={cn(
            "min-w-0 flex-1 bg-transparent text-ivory placeholder:text-ivory-muted/80 focus:outline-none",
            isHeader ? "text-sm" : "text-sm sm:text-base sm:placeholder:text-ivory-faint"
          )}
        />
        {query && (
          <button
            type="button"
            aria-label={t("clearSearch")}
            title={t("clearSearch")}
            onMouseDown={(event) => event.preventDefault()}
            onClick={clearSearch}
            className="flex size-6 shrink-0 items-center justify-center rounded-full text-ivory-faint transition-colors hover:bg-surface hover:text-ivory focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
          >
            <svg viewBox="0 0 24 24" className="size-3.5" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
            </svg>
          </button>
        )}
        {showSuggestions && !query && (
          <div className="hidden shrink-0 items-center gap-1.5 border-l border-ink-border/70 pl-3 lg:flex">
            {["Teddy Afro", "Toronto", "Rophnan"].map((suggestion) => (
              <button
                key={suggestion}
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  setQuery(suggestion);
                  setOpen(true);
                }}
                className="rounded-full border border-gold/25 bg-gold/[0.08] px-2.5 py-1 text-xs text-gold-dim transition-colors hover:border-green/35 hover:text-green"
              >
                {suggestion}
              </button>
            ))}
          </div>
        )}
      </div>

      {open && query.trim() && (
        <div
          className={cn(
            "z-50 mt-2 w-full overflow-hidden rounded-xl border border-ink-border bg-white shadow-[0_24px_60px_-28px_rgb(var(--shadow-color))]",
            isHeader ? "absolute left-0 top-full" : "relative"
          )}
        >
          {!hasResults && (
            <p className="px-5 py-4 text-sm text-ivory-faint">{t("noMatches", { query })}</p>
          )}
          {activeResults.profiles.length > 0 && (
            <div className="border-b border-ink-border/70 p-2">
              <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-ivory-faint">People</p>
              {activeResults.profiles.slice(0, 5).map((a) => (
                <Link
                  key={a.id}
                  href={profileHref(a)}
                  onClick={selectResult}
                  className="block rounded-lg px-3 py-2 text-sm text-ivory hover:bg-surface hover:text-green"
                >
                  {a.name}
                </Link>
              ))}
            </div>
          )}
          {activeResults.cities.length > 0 && (
            <div className="p-2">
              <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wide text-ivory-faint">{t("cities")}</p>
              {activeResults.cities.slice(0, 5).map((c) => (
                <Link
                  key={c.id}
                  href={`/cities/${c.slug}`}
                  onClick={selectResult}
                  className="block rounded-lg px-3 py-2 text-sm text-ivory hover:bg-surface hover:text-green"
                >
                  {c.name}, {c.country}
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
